import React, { Dispatch, ReactNode, useEffect, useState } from "react"
import { CardElement } from "@stripe/react-stripe-js"
import { CreatePaymentMethodCardData, Stripe, StripeCardElementChangeEvent, StripeElements } from "@stripe/stripe-js"
import StyledDiv from './styles'
import { Button } from "../Button"
import { LiteralsService, UserService } from "../../services"
import { Spinner } from "../Spinner"
import { confirmNewReservationAction, setNotificationAction, setPaymentMethodId, setSignUpCallingCodeAction, setSignUpCountryAction, setSignUpEmailAction, setSignUpLastNameAction, setSignUpNameAction, setSignUpPhoneAction, setStripeAccountId, setStripeCustomerIds, setToggleCountryPickerModalAction } from "../../store/actions"
import { useSelector } from "react-redux"
import { IStoreState } from "../../store/states"
import { Application } from "../../config"
import { Link, useLocation } from "react-router-dom"
import { isEmailValid, isTelephoneValid } from "../../helpers"
import { IPaymentActions, IUser } from "../../models"
import { ICountry } from "../../models/country"
import { Radiobutton, ValidatedInput } from ".."
import CountryPickerModal from "../../containers/CountryPickerModal"
import countries from '../../countries.json'
import { appsPrivacyAndTermsOptionsSelected } from "../../helpers/app"
import { images } from "../../images"
import { HttpStatusCode } from "axios"

interface IProps {
  stripe: Stripe
  elements: StripeElements
  amount: number
  isEdit: boolean
  pickedService?: number | null
  isConfig?: boolean
  privacyPolicyUrl?: string
  dispatch: Dispatch<any>
  updateUserInfo: () => void
  close: () => void
}

const { isIframe } = Application

export const AddPaymentMethodForm: React.FC<IProps> = ({
  stripe,
  elements,
  amount,
  isEdit,
  pickedService,
  isConfig,
  dispatch,
  updateUserInfo,
  close,
  privacyPolicyUrl
}) => {

  const [cardDetailsComplete, setCardDetailsComplete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [repeatEmail, setRepeatEmail] = useState('')
  const [validatingEmail, setValidatingEmail] = useState(false)
  const [useExistingPayment, setUseExistingPayment] = useState(true)
  const [user, setUser] = useState<IUser | null>(null)
  const [privacyAndTermsChecked, setPrivacyAndTermsChecked] = useState(false)

  const userSignupSelector = useSelector((state: IStoreState) => state.signUp)
  const userInfoSelector = useSelector((state: IStoreState) => state.userInfo)
  const newReservationSelector = useSelector((state: IStoreState) => state.newReservation)

  const location = useLocation()

  useEffect(() => {
    if (location.state && location.state.userData) {
      const userData = location.state.userData
      onNameChange(userData.name || '')
      onLastNameChange(userData.lastName || '')
      onEmailChange(userData.email || '')
      onPhoneChange('')
    }
  }, [])

  useEffect(() => {
    if (userInfoSelector.email !== '' && !userSignupSelector.country) {
      const initialCountry = countries.find((country) => country.code.toLowerCase() === userInfoSelector.locale) || countries.find((country) => country.code === 'ES')
      onCountryChange(initialCountry!)
    }
  }, [userInfoSelector.email])

  useEffect(() => {
    if (loading && !newReservationSelector.isConfirmingReservation) setLoading(false)
  }, [newReservationSelector.isConfirmingReservation])

  const dataForm = (): ReactNode => {
    return (
      <div className='dataform-container'>
        <div className='dataform-horizontal'>
          <ValidatedInput
            name="name"
            label={ LiteralsService.get('name', true) }
            value={ userSignupSelector.name }
            onChange={ onNameChange }
          />
          <ValidatedInput
            name="surname"
            label={ LiteralsService.get('surname', true) }
            value={ userSignupSelector.lastName }
            onChange={ onLastNameChange }
          />
        </div>
        <div className='dataform-horizontal'>
          <ValidatedInput
            name="email"
            label={ LiteralsService.get('email', true) }
            value={ userSignupSelector.email }
            onChange={ onEmailChange }
            error={ !isEmailValid(userSignupSelector.email) }
          />
          {
            validatingEmail &&
            <div className="icon-container">
              <Spinner />
            </div>
          }
        </div>
        <div className='dataform-horizontal'>
          <ValidatedInput
            name="repeatEmail"
            label={ LiteralsService.get('repeatEmail', true) }
            value={ repeatEmail }
            onChange={ onRepeatEmailChange }
            error={ userSignupSelector.email !== repeatEmail }
          />
        </div>
        <div className='dataform-horizontal'>
          <ValidatedInput
            name="phone"
            phone={ true }
            label={ LiteralsService.get('phone', true) }
            value={ userSignupSelector.phone }
            onChange={ onPhoneChange }
            error={ !isTelephoneValid(userSignupSelector.phone) }
            country={userSignupSelector.country}
          />
          <CountryPickerModal
            selectCountry={(country: ICountry) => onCountryChange(country) }
          ></CountryPickerModal>
        </div>
      </div>
    )
  }

  const onNameChange = (name: string) => {
    dispatch(setSignUpNameAction(name))
  }

  const onLastNameChange = (lastName: string) => {
    dispatch(setSignUpLastNameAction(lastName))
    
  }
  const onEmailChange = (email: string) => {
    dispatch(setSignUpEmailAction(email))
  }

  const onRepeatEmailChange = (email: string) => {
    setRepeatEmail(email)
  }

  const onPhoneChange = (phone: string) => {
    dispatch(setSignUpPhoneAction(phone))
  }
  
  const onCountryChange = (country: ICountry) => {
    dispatch(setSignUpCallingCodeAction(country?.dial_code))
    dispatch(setSignUpCountryAction(country))
  }
    
  const onCardChange = (event: StripeCardElementChangeEvent) => {
    setCardDetailsComplete(event.complete)
  }

  const isDataFormValid = () => {
    return userSignupSelector.name !== '' && userSignupSelector.lastName !== '' && isEmailValid(userSignupSelector.email) && (repeatEmail === userSignupSelector.email) && isTelephoneValid(userSignupSelector.phone) && privacyAndTermsChecked
  }

  const savePaymentMethod = async() => {
    elements.update({
      amount: amount * 100
    })

    setLoading(true)

    if (stripe && elements) {
      try {
        let paymentMethodId = ''

        if (useExistingPayment && user && user.paymentMethodId) {
          paymentMethodId = user.paymentMethodId
        } else {
          const card = elements.getElement(CardElement)!
          const { token } = await stripe.createToken(card, { currency: 'eur' })
          const cardPaymentMethodData: CreatePaymentMethodCardData = {
            type: 'card',
            card: token ? { token: token.id } : card
          }
          
          const { paymentMethod, error: createPaymentMethodError } = await stripe.createPaymentMethod(cardPaymentMethodData)
          if (createPaymentMethodError || !paymentMethod?.id) {
            throw { data: [{ declineCode: createPaymentMethodError?.decline_code || createPaymentMethodError?.code }] }
          }
          paymentMethodId = paymentMethod.id
        }

        dispatch(setPaymentMethodId(paymentMethodId))
        if (isIframe) {
          // TODO: if consider all ticketing types: switch case of ticketing type & send correct IPaymentActions
          dispatch(confirmNewReservationAction(IPaymentActions.PAY))
        } else {
          if (isConfig) {
            await configDisplay(paymentMethodId)
          } else {
            await newReservationConfirmationStep(paymentMethodId)
          }
        }
        !isIframe && updateUserInfo()
      } catch(error: any) {
        let title = 'paymentError'
        switch (error.status) {
          case HttpStatusCode.BadRequest:
            title = 'paymentNotActivated'
            break
          default:
            title = `${error.data[0].declineCode || 'generic_decline'}`
        }

        dispatch(setNotificationAction({
          isError: true,
          label: LiteralsService.get(
            title,
            true
          )
        }))
      }
    }
  }

  const newReservationConfirmationStep = async (paymentMethodId: string) => {
    await UserService.postCustomer(paymentMethodId, pickedService!).then((res: any) => {
      dispatch(setStripeCustomerIds(res.customerId))
      dispatch(setStripeAccountId(res.accountId))
      dispatch(setNotificationAction({
        isError: false,
        label: LiteralsService.get('paymentMethodAdded', true),
      }))
      close()
    })
    .finally(() => {
      setLoading(false)
    })

    setCardDetailsComplete(false)
  }

  const configDisplay = async (paymentMethodId: string) => {
    if (userInfoSelector.stripeIds) {     // With customer
      await UserService.setPaymentMethodCustomer(paymentMethodId).then(async (res: any) => {
        if (res.fr && res.b) {
          await getResults(res)
        } else {
          dispatch(setStripeCustomerIds(res))
          dispatch(setNotificationAction({
            isError: false,
            label: LiteralsService.get('paymentMethodUpdated', true)
          }))
          close() 
        }
      })
      .finally(() => {
        setLoading(false)
      })
    } else {      // Without customer
      await UserService.postCustomer(paymentMethodId, pickedService!).then((res: any) => {
        dispatch(setStripeCustomerIds(res.customerId))
        dispatch(setStripeAccountId(res.accountId))
        dispatch(setNotificationAction({
          isError: false,
          label: LiteralsService.get('paymentMethodAdded', true)
        }))
      })
      .finally(() => {
        setLoading(false)
      })
    }
    setCardDetailsComplete(false)
    setLoading(false)
  }

  const getResults = async (res: any) => {
      res.fr.onload = async () => {
          const result = await JSON.parse(res.fr.result.toString()) 
          dispatch(setNotificationAction({
              isError: true,
              label: LiteralsService.get(`${result[0].errorCode}.${result[0].declineCode}`, true)
          }))
      }
      res.fr.readAsText(res.b)
  }

  const showCardElement = () => {
    // TODO: if consider all ticketing types: switch case of ticketing type & show or not card element
    if (!isIframe) return true
    if (!isEmailValid(userSignupSelector.email) || repeatEmail !== userSignupSelector.email) return false
    return true
  }

  const getButtonText = () => {
    // TODO: if consider all ticketing types: switch case of ticketing type & display correct text
    if (isIframe) return 'pay'
    return isEdit ? 'change' : 'add'
  }

  return (
    <StyledDiv>
      <div className="modal-content">
        { isIframe && dataForm() }
        {
          user && user.paymentMethodId && user.paymentMethodLastFour &&
          <>
            <div className="cards-container" onClick={() => {
              setUseExistingPayment(true)
              setCardDetailsComplete(true)
            }}>
              <Radiobutton
                selected={useExistingPayment}
                label=""
              />
              <div className='active-card'>
                <span>
                  { `${LiteralsService.get('activeCard', true)}: ` }
                </span>
                <span className='active-card-info'>
                  { `  xxxx xxxx xxxx ${ user?.paymentMethodLastFour }` }
                </span>
              </div>
            </div>
            <div className='cards-container' onClick={() => {
              setUseExistingPayment(false)
              setCardDetailsComplete(false)
            }}>
              <Radiobutton
                selected={!useExistingPayment}
                label=""
              />
              <span className="new-card">
                { `${LiteralsService.get('newCard', true)}` }
              </span>
            </div>
          </>
        }
        {
          showCardElement() &&
          <CardElement
            options={{
              classes: {
                base: 'stripe-card-container'
              },
              hidePostalCode: true,
              disableLink: true
            }}
            onChange={ onCardChange }
          />
        }
        {
          isIframe &&
          <div className="checkbox-wrapper">
            <div className={`checkbox-container ${privacyAndTermsChecked ? 'selected' : ''}`} onClick={ () => setPrivacyAndTermsChecked(!privacyAndTermsChecked) }>
              <img src={ privacyAndTermsChecked ? images['checkboxChecked'] : images['checkboxUnchecked']} alt={'TermsPrivacyCheckbox'} />
            </div>
            <span>
              { LiteralsService.get('terms', true) }
              <Link
                className="link"
                to={{ pathname: appsPrivacyAndTermsOptionsSelected?.terms }}
                target="_blank"
              >
                { LiteralsService.get('termsLink', true) }
              </Link>
              { ` ${LiteralsService.get('and', false)} ` } 
              <Link
                className="link"
                to={{ pathname: privacyPolicyUrl || appsPrivacyAndTermsOptionsSelected?.privacy }}
                target="_blank"
              >
                { LiteralsService.get('privacyPolicy', true) }
              </Link>
            </span>
          </div>
        }
      </div>
      <div className='buttons-container'>
        {
          loading ? 
          <Spinner /> 
          :
          <Button 
            title={ LiteralsService.get(getButtonText(), true) }
            onPress={ savePaymentMethod }
            isDisabled={ !cardDetailsComplete || (isIframe && !isDataFormValid()) }
            filled
            upper
          />
        }
      </div>
    </StyledDiv>
  )
}